// LeadsFlow.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FiChevronRight, FiAlertTriangle, FiRefreshCw, FiLoader, FiX,
  FiPhone, FiMail, FiUser, FiHome, FiGlobe, FiMessageSquare, FiHeadphones, FiPlay
} from "react-icons/fi";

const API_BASE = "/leads";

async function apiFetch(path, { method = "GET", body, headers } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  const ct = res.headers.get("content-type");
  return ct && ct.includes("application/json") ? res.json() : res.text();
}

function Chip({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-gray-100 text-gray-800", blue: "bg-brand-100 text-brand-800",
    green: "bg-green-100 text-green-800", yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-800", purple: "bg-brand-100 text-brand-800",
  };
  return <span className={`px-2 py-0.5 text-xs rounded-full ${tones[tone] || tones.gray}`}>{children}</span>;
}
const Divider = () => <div className="h-px bg-gray-200 my-6" />;

const STEP_LABELS = {
  S1_GREET: "Welcome",
  S1_EXPLAIN: "About YouResto",
  S2_Q1: "Business Type",
  S2_Q2: "Order Mode",
  S2_Q3: "Location",
  S2_Q4: "Staff Size",
  S2_Q5: "Biggest Headache",
  S3_CONTACT: "Contact Details",
  S4_MENU: "Feature Menu",
  S5_HELP: "Talk to a Human",
  S6_CTA: "Get Started",
  END: "Summary & Offers",
};
const STEP_ORDER = [
  "S1_GREET","S1_EXPLAIN","S2_Q1","S2_Q2","S2_Q3","S2_Q4","S2_Q5","S3_CONTACT","S4_MENU","S5_HELP","S6_CTA"
];

const PRODUCT_META = {
  billing:        { title: "Billing & Invoicing" },
  inventory:      { title: "Inventory / Supplies" },
  reports:        { title: "Reports / Profits" },
  qr_payments:    { title: "QR / Payments" },
  staff_hr:       { title: "Staff / HR Management" },
  online_orders:  { title: "Online Orders" },
  whatsapp_tools: { title: "WhatsApp Marketing Tools" },
  loans_offers:   { title: "Loans & Cashback" },
  demo:           { title: "Watch Demo", video: "https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1" },
};

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0, v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function LeadsFlow() {
  const [sessionId, setSessionId] = useState("");
  const [currentStep, setCurrentStep] = useState(null);
  const [score, setScore] = useState(0);
  const [ended, setEnded] = useState(false);
  const [final, setFinal] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [answersMap, setAnswersMap] = useState({});
  const [company, setCompany] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoUrl, setDemoUrl] = useState("");

  useEffect(() => {
    const sid = uuid();
    setSessionId(sid);
    (async () => {
      try {
        const started = await apiFetch("/start", { method: "POST", body: { session_id: sid } });
        setCurrentStep(started.next_step);
        setScore(started.score || 0);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  const progress = useMemo(() => {
    if (!currentStep || ended) {
      if (ended) return { current: STEP_ORDER.length + 1, total: STEP_ORDER.length + 1, pct: 100 };
      return { current: 1, total: STEP_ORDER.length + 1, pct: 0 };
    }
    const idx = Math.max(0, STEP_ORDER.indexOf(currentStep.id));
    const total = STEP_ORDER.length + 1;
    const pct = Math.round((idx / total) * 100);
    return { current: idx + 1, total, pct };
  }, [currentStep, ended]);

  function rememberAnswer(stepId, value) {
    setAnswersMap((prev) => ({ ...prev, [stepId]: value }));
  }

  async function submitAnswer(answer) {
    if (!sessionId || !currentStep) return;
    setSubmitting(true);
    try {
      rememberAnswer(currentStep.id, answer);
      const resp = await apiFetch("/answer", {
        method: "POST",
        body: { session_id: sessionId, step_id: currentStep.id, answer },
      });

      if (resp.completed) {
        setFinal(resp);
        setEnded(true);
        setCurrentStep(null);
        setScore(resp.score ?? score);
      } else if (resp.next_step) {
        setCurrentStep(resp.next_step);
        setScore(resp.score ?? score);
        if (resp.next_step.id !== "S3_CONTACT") {
          setCompany(""); setAddress(""); setWebsite(""); setContactName(""); setEmail(""); setPhone("");
        }
      }
      setError("");
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  // ------- keep END card in sync after completion -------
  function syncFinalFromServerPayload(r) {
    if (!ended) return;
    setFinal((prev) => prev ? { ...prev,
      score: r?.score ?? prev.score ?? 0,
      bucket: r?.bucket ?? prev.bucket ?? "COLD",
    } : prev);
  }

  async function clickMenuItem(itemId) {
    if (!sessionId) return;
    try {
      const r = await apiFetch("/menu_click", {
        method: "POST",
        body: { session_id: sessionId, item_id: itemId },
      });
      setScore(r.score ?? score);
      syncFinalFromServerPayload(r);
      if (itemId === "demo") {
        const url = PRODUCT_META["demo"].video;
        if (url) { setDemoUrl(url); setDemoOpen(true); }
      }
    } catch { /* ignore */ }
  }

  async function sendMeaningfulMessage() {
    if (!sessionId || !message.trim()) return;
    try {
      const r = await apiFetch("/meaningful_message", {
        method: "POST",
        body: { session_id: sessionId },
      });
      setScore(r.score ?? score);
      syncFinalFromServerPayload(r);
      setMessage("");
    } catch { /* ignore */ }
  }

  async function escalate() {
    if (!sessionId) return;
    setSubmitting(true);
    try {
      const r = await apiFetch("/escalate", {
        method: "POST",
        body: { session_id: sessionId },
      });
      setScore(r.score ?? score);
      syncFinalFromServerPayload(r);
      await submitAnswer("done");
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  function renderSingleChoice(step) {
    const opts = step.options || [];
    return (
      <div className="space-y-3">
        {opts.map((opt) => (
          <button
            key={opt}
            disabled={submitting}
            onClick={() => submitAnswer(opt)}
            className="w-full text-left px-4 py-3 border rounded-lg hover:bg-gray-50 transition flex items-center justify-between border-gray-200"
          >
            <span className="text-gray-800">{opt}</span>
            <FiChevronRight className="text-gray-400" />
          </button>
        ))}
      </div>
    );
  }

  function renderText(step) {
    return (
      <form
        onSubmit={(e) => { e.preventDefault(); const val = e.currentTarget.elements["value"].value.trim(); if (val) submitAnswer(val); }}
        className="flex gap-3"
      >
        <input name="value" placeholder="Type your answer"
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500" />
        <button type="submit" disabled={submitting}
          className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-ink rounded-lg flex items-center gap-2">
          {submitting ? <FiLoader className="animate-spin" /> : null}
          Submit
        </button>
      </form>
    );
  }

  function renderContactForm() {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Company Name</label>
            <div className="relative">
              <FiHome className="absolute left-3 top-3 text-gray-400" />
              <input value={company} onChange={(e)=>setCompany(e.target.value)} placeholder="Company Pvt Ltd"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"/>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Website</label>
            <div className="relative">
              <FiGlobe className="absolute left-3 top-3 text-gray-400" />
              <input value={website} onChange={(e)=>setWebsite(e.target.value)} placeholder="https://example.com"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"/>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">Address</label>
          <textarea value={address} onChange={(e)=>setAddress(e.target.value)} placeholder="Full address"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"/>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Contact Name</label>
            <div className="relative">
              <FiUser className="absolute left-3 top-3 text-gray-400" />
              <input value={contactName} onChange={(e)=>setContactName(e.target.value)} placeholder="Your name"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"/>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Email</label>
            <div className="relative">
              <FiMail className="absolute left-3 top-3 text-gray-400" />
              <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="you@company.com"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"/>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Phone</label>
            <div className="relative">
              <FiPhone className="absolute left-3 top-3 text-gray-400" />
              <input type="tel" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="+91 9xxxxxxxxx"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"/>
            </div>
          </div>
        </div>

        <div className="pt-2">
          <button
            disabled={submitting}
            onClick={() => submitAnswer({ company_name: company, address, website, contact_name: contactName, email, phone })}
            className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-ink rounded-lg flex items-center gap-2"
          >
            {submitting ? <FiLoader className="animate-spin" /> : null}
            Save & Continue
          </button>
        </div>
      </div>
    );
  }

  function renderMenu(step) {
    const items = step.menu_items || [];
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((mi) => (
            <div key={mi.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-sm transition">
              <div className="font-semibold text-gray-900 mb-1">{mi.title}</div>
              <div className="text-sm text-gray-600 mb-3">Explore how “{mi.title}” helps your business.</div>
              <button
                onClick={() => clickMenuItem(mi.id)}
                className="px-3 py-1.5 text-sm bg-brand-500 hover:bg-brand-600 text-ink rounded-lg inline-flex items-center gap-2"
              >
                {mi.id === "demo" ? (<><FiPlay /> Watch Demo</>) : "Learn More"}
              </button>
            </div>
          ))}
        </div>
        <div className="pt-2">
          <button disabled={submitting} onClick={() => submitAnswer("ok")}
            className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-ink rounded-lg flex items-center gap-2">
            {submitting ? <FiLoader className="animate-spin" /> : null}
            Continue
          </button>
        </div>
      </div>
    );
  }

  function renderAction() {
    return (
      <div className="space-y-4">
        <div className="text-gray-700">Need help choosing a plan or setting things up? We can connect you with a person right away.</div>
        <div className="flex gap-2">
          <button disabled={submitting} onClick={escalate}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2">
            <FiHeadphones /> Talk to a Human
          </button>
          <button disabled={submitting} onClick={() => submitAnswer("skip")}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg">
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  function renderQuestion() {
    if (!currentStep) return null;
    const type = (currentStep.question_type || "single_choice").toLowerCase();
    if (type === "single_choice" || type === "yes_no") return renderSingleChoice(currentStep);
    if (type === "text" || type === "number") return renderText(currentStep);
    if (type === "contact_form") return renderContactForm();
    if (type === "menu") return renderMenu(currentStep);
    if (type === "action") return renderAction();
    return (
      <div className="space-y-4">
        <p className="whitespace-pre-line text-gray-800">{currentStep.question_text || currentStep.text}</p>
        <button disabled={submitting} onClick={() => submitAnswer("ok")}
          className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-ink rounded-lg flex items-center gap-2">
          Continue
        </button>
      </div>
    );
  }

  const stepLabel = currentStep ? (STEP_LABELS[currentStep.id] || "") : ended ? STEP_LABELS.END : "";

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mx-auto" style={{ maxWidth: 1100 }}>
          {/* Header */}
          <div className="flex items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Smart Lead Assistant</h1>
              <p className="text-gray-600 mt-1">Welcome — let’s tailor your solution 🚀</p>
            </div>
            <div className="flex items-center gap-2">
              {sessionId && <Chip tone="blue">session: {sessionId.slice(0, 8)}…</Chip>}
              <button onClick={() => window.location.reload()}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                <FiRefreshCw className="w-4 h-4" /> Reset
              </button>
            </div>
          </div>

          {/* Progress */}
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-6">
            <div className="h-full bg-brand-500" style={{ width: `${progress.pct}%` }} />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg mb-6 flex items-center gap-2 text-red-800">
              <FiAlertTriangle /> <span>{error}</span>
              <button className="ml-auto" onClick={() => setError("")}><FiX /></button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
            {/* LEFT */}
            <div className="lg:col-span-2">
              {!ended ? (
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-2 text-sm text-gray-500">
                    {`Step ${progress.current} of ${progress.total}`} — {stepLabel}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 whitespace-pre-line">
                    {currentStep?.question_text || currentStep?.text}
                  </h3>
                  {renderQuestion()}

                  {/* Meaningful message box */}
                  <div className="mt-6">
                    <label className="block text-sm text-gray-700 mb-1">Have a question?</label>
                    <div className="flex items-stretch gap-2">
                      <div className="relative flex-1">
                        <FiMessageSquare className="absolute left-3 top-3 text-gray-400" />
                        <input value={message} onChange={(e) => setMessage(e.target.value)}
                          placeholder="Type here and click Send to get priority support"
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"/>
                      </div>
                      <button onClick={sendMeaningfulMessage}
                        className="px-4 py-2 bg-gray-900 hover:bg-black text-white rounded-lg">
                        Send
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Meaningful questions add points to your profile automatically.</div>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">All set! 🎉</h3>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Chip tone={final?.bucket === "SQL" ? "green" : final?.bucket === "MQL" ? "yellow" : "gray"}>
                      {final?.bucket || "COLD"}
                    </Chip>
                    <Chip tone="purple">Score: {final?.score ?? 0}</Chip>
                  </div>

                  {final?.offers ? (
                    <div className="space-y-4">
                      <div>
                        <div className="text-sm text-gray-500">{final.offers.subtitle}</div>
                        <h3 className="text-xl font-semibold text-gray-900">{final.offers.title}</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {(final.offers.products || []).map((pid) => (
                          <div key={pid} className="border border-gray-200 rounded-xl p-4 hover:shadow-sm transition">
                            <div className="font-semibold text-gray-900 mb-1">
                              {(PRODUCT_META[pid]?.title) || pid}
                            </div>
                            <div className="text-sm text-gray-600 mb-3">
                              {pid === "demo" ? "See how it works in 60 seconds." : "Click to explore how this feature helps your business."}
                            </div>
                            <button onClick={() => clickMenuItem(pid)}
                              className="px-3 py-1.5 text-sm bg-brand-500 hover:bg-brand-600 text-ink rounded-lg inline-flex items-center gap-2">
                              {pid === "demo" ? (<><FiPlay /> Watch Demo</>) : "Learn More"}
                            </button>
                          </div>
                        ))}
                      </div>
                      {Array.isArray(final.offers.terms) && final.offers.terms.length > 0 && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-gray-800">Terms:</span> {final.offers.terms.join(" · ")}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-700">Our team will reach out soon. You can close this page.</p>
                  )}
                </div>
              )}
            </div>

            {/* RIGHT: Journey */}
            <div className="lg:col-span-1">
              <div className="mt-0 bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Journey</h3>
                <ul className="space-y-2 text-sm">
                  {[...STEP_ORDER, "END"].map((sid, idx) => {
                    const isCurrent = !ended && currentStep?.id === sid;
                    const answered = answersMap[sid];
                    const label = STEP_LABELS[sid] || `Step ${idx + 1}`;
                    const answeredText =
                      Array.isArray(answered) ? answered.join(", ")
                      : (typeof answered === "object" && answered !== null)
                        ? Object.entries(answered).filter(([,v])=>v).map(([k,v])=>`${k.replace(/_/g," ")}: ${v}`).join(", ")
                        : (answered ?? "");
                    const isDone = answered || (ended && sid === "END");
                    return (
                      <li key={sid}
                        className={`flex items-start gap-2 p-2 rounded-lg ${isCurrent ? "bg-brand-50 border-l-4 border-brand-500" : isDone ? "bg-gray-50" : "hover:bg-gray-50"}`}>
                        <span className={`mt-1.5 w-2 h-2 rounded-full ${isCurrent ? "bg-yellow-500" : isDone ? "bg-brand-500" : "bg-gray-300"}`} />
                        <div className="flex-1">
                          <div className="text-gray-800">{label}</div>
                          <div className="text-gray-500 text-xs">
                            {answered ? `Answer: ${answeredText}` : (sid === "END" ? (ended ? "Completed" : "Pending") : "Pending")}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <Divider />
                <div className="text-xs text-gray-500">
                  <div>Session ID: {sessionId || "—"}</div>
                  <div>Current Score: {score}</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Demo Modal */}
      {demoOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold">Product Demo</div>
              <button onClick={() => { setDemoOpen(false); setDemoUrl(""); }} className="p-2 rounded hover:bg-gray-100">
                <FiX />
              </button>
            </div>
            <div className="aspect-video w-full">
              {demoUrl ? (
                <iframe src={demoUrl} title="Demo Video" className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">Loading…</div>
              )}
            </div>
            <div className="p-4 flex justify-end">
              <button onClick={() => { setDemoOpen(false); setDemoUrl(""); }}
                className="px-4 py-2 bg-gray-900 hover:bg-black text-white rounded-lg">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
